/* eslint-disable react-hooks/exhaustive-deps */
import {
  ArtHeroSearchCard,
  CollectionSearchModal,
  CollectionSearchResultSection,
  ContentNotFound,
  Hero,
  Layout,
} from '@common'
import {
  ICollectionSearchScreen,
  IMetaDataCollectionSearchPage,
} from '@interfaces'
import { eMUSEUM_PAGINATION_LIMIT } from '@lib/const'
import { eMuseumSearch } from '@services'
import { useStore } from '@store'
import { calculateTotalPageCount, encodeURLParamValue } from '@utils'
import { observer } from 'mobx-react-lite'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

const CollectionsSearchScreen = observer(
  ({ pageData, metaData, global: { navigation } }: ICollectionSearchScreen) => {
    const { t } = useTranslation()
    const { asPath, query } = useRouter()
    const {
      artSearch: {
        setSearchTerm,
        setSearchTitle,
        setSelectedInCollectionOptions,
        clearAllFilterOnAdvancedFilter,
      },
    } = useStore()

    useEffect(() => {
      if (!query?.isAdvancedSearch) {
        clearAllFilterOnAdvancedFilter()
      }

      if (query?.searchTerm && query?.searchTerm !== '') {
        setSearchTerm(query?.searchTerm.toString())
      }

      if (
        query?.isAdvancedSearch &&
        query?.searchTitle &&
        query?.searchTitle !== ''
      ) {
        setSearchTitle(query?.searchTitle.toString())
      }

      if (
        query?.isAdvancedSearch &&
        query?.selectedInCollectionOptions &&
        query?.selectedInCollectionOptions !== ''
      ) {
        setSelectedInCollectionOptions(
          query?.selectedInCollectionOptions.toString()
        )
      }

      return () => {
        clearAllFilterOnAdvancedFilter()
      }
    }, [asPath, query])

    if (pageData.error) {
      return (
        <Layout
          title={t('collections-and-research:search.title')}
          navigation={navigation}
          path={asPath}
        >
          <ContentNotFound />
        </Layout>
      )
    }

    return (
      <Layout
        title={t('collections-and-research:search.title')}
        navigation={navigation}
        path={asPath}
      >
        <Hero
          bgImageUrl="https://drupal.sakipsabancimuzesi.org/sites/muze.sabanciuniv.edu/files/2021-10/arsivler-bnr.jpg"
          subTitle={t('collections-and-research:search.banner.subTitle')}
          title={t('collections-and-research:search.banner.title')}
        >
          <ArtHeroSearchCard />
        </Hero>
        <CollectionSearchResultSection
          results={pageData.data}
          metaData={metaData}
        />
        <CollectionSearchModal />
      </Layout>
    )
  }
)

export default CollectionsSearchScreen

interface ISSR {
  locale: string
  query: IMetaDataCollectionSearchPage
}

export async function getServerSideProps({ locale, query }: ISSR) {
  const page = query?.page || '1'
  const totalPage = '1'
  const isAdvancedSearch = query?.isAdvancedSearch || 'false'
  const sort = query?.sort || 'Relevance'
  const totalCount = 0
  const limit = eMUSEUM_PAGINATION_LIMIT

  const metaData = {
    page,
    totalPage,
    sort,
    totalCount,
    limit,
  }

  const pageData = {
    data: null,
    error: null,
  }

  try {
    // check is common search
    if (isAdvancedSearch === 'false') {
      const searchTerm = query?.searchTerm || ''
      const searchResult = await eMuseumSearch.commonSearchOnObjects(
        locale,
        encodeURLParamValue(searchTerm),
        `page=${metaData.page}&sort=${sort}`
      )
      pageData.data = searchResult.results
      metaData.totalCount = searchResult.count
      metaData.totalPage = calculateTotalPageCount(
        metaData.totalCount,
        metaData.limit
      )
    }

    // check is advanced search
    if (isAdvancedSearch === 'true') {
      const searchTitle = query?.searchTitle
      const searchArtist = query?.searchArtist
      const searchInvNo = query?.searchInvNo
      const searchDescription = query?.searchDescription
      const selectedInCollectionOptions = query?.selectedInCollectionOptions
      const beginDate = query?.beginDate
      const endDate = query?.endDate

      let filterQueryParams = ''

      if (beginDate) {
        filterQueryParams = filterQueryParams + `beginDate:${beginDate};`
      }

      if (endDate) {
        filterQueryParams = filterQueryParams + `endDate:${endDate};`
      }

      if (selectedInCollectionOptions) {
        filterQueryParams =
          filterQueryParams + `collections:${selectedInCollectionOptions};`
      }

      if (searchTitle && searchTitle !== '') {
        filterQueryParams = filterQueryParams + `title:${searchTitle};`
      }

      if (searchArtist && searchArtist !== '') {
        filterQueryParams = filterQueryParams + `people:${searchArtist};`
      }

      if (searchInvNo && searchInvNo !== '') {
        filterQueryParams = filterQueryParams + `invno:${searchInvNo};`
      }

      if (searchDescription && searchDescription !== '') {
        filterQueryParams =
          filterQueryParams + `description:${searchDescription};`
      }

      const searchResult = await eMuseumSearch.advancedSearchOnObjects(
        locale,
        encodeURIComponent(filterQueryParams),
        `page=${metaData.page}&sort=${sort}`
      )
      pageData.data = searchResult.results
      metaData.totalCount = searchResult.count
      metaData.totalPage = calculateTotalPageCount(
        metaData.totalCount,
        metaData.limit
      )
    }
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData, metaData },
  }
}
